import { isAlertEnvelope, type AlertEnvelope } from '@fleetcore/shared/telemetry/alerts'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '../../auth/use-auth'
import { LiveSocket, type SocketFactory } from '../../lib/socket'
import { alertQueryKeys } from './queries'

/**
 * Live arrival, over the EXISTING gateway.
 *
 * Rule 8 says in-app notifications go through the socket.io gateway the live map
 * already owns, not through a second transport. That is what this does: it opens a
 * `LiveSocket` — the same class `features/map/use-live-fleet.ts` uses — and listens
 * for `WS_EVENTS.event`, which the API's realtime module relays from the worker's
 * `fleetcore:live:alerts` publish. No new client, no polling loop of its own, no
 * second protocol.
 *
 * ─── WHY THIS OPENS ITS OWN SOCKET RATHER THAN SHARING THE MAP'S ────────────
 *
 * `lib/socket.ts` exposes a class, not a singleton, and it is not this phase's file.
 * The map builds one instance while it is mounted and tears it down on unmount; this
 * hook does the same for the alerts surfaces. In practice the two screens are never
 * mounted together — they are separate routes — so the app holds one connection at a
 * time. Making that structural rather than incidental means promoting the socket to a
 * shared singleton in `lib/`, which is recorded in NOTES-PHASE7-WEB.md §6 as the one
 * change this module would ask for.
 *
 * ─── AN ARRIVING ALERT DOES NOT REFETCH THE FEED ────────────────────────────
 *
 * The envelope carries everything a toast and an unread badge need. Invalidating the
 * feed on every envelope would turn a fleet-wide event — five hundred vehicles going
 * quiet, forty overspeeds on one motorway run — into five hundred full page requests,
 * which is the failure `features/map` documents and designs against. The feed is
 * refreshed on a bounded schedule instead, and the unread counter is what tells the
 * user there is something new to look at.
 *
 * ─── THE BUFFER IS BOUNDED, AND IT SHEDS THE OLDEST ─────────────────────────
 *
 * Same discipline as the worker's ingress. A tab left open over a bad night must not
 * grow an unbounded array of envelopes; the newest are the ones a human would act on.
 */

/** How many arrivals are kept for the toast stack and the "new since" list. */
export const ALERT_STREAM_BUFFER = 50

/** How many toasts are shown at once. Beyond this the counter carries the rest. */
export const MAX_VISIBLE_TOASTS = 3

export type AlertStreamStatus = 'connecting' | 'live' | 'degraded'

export interface UseAlertStreamOptions {
  /** Test seam: a fake socket factory. Production uses the real socket.io client. */
  socketFactory?: SocketFactory
  enabled?: boolean
  /** Called for every accepted envelope — the toast host subscribes through this. */
  onAlert?: (envelope: AlertEnvelope) => void
}

export interface UseAlertStreamResult {
  readonly status: AlertStreamStatus
  /** True while the socket is NOT carrying the screen, so the feed should poll. */
  readonly degraded: boolean
  readonly unreadCount: number
  readonly recent: readonly AlertEnvelope[]
  /** Clears the badge. Called when the feed is actually looked at. */
  readonly markRead: () => void
  readonly dismiss: (alertId: string) => void
}

export function useAlertStream(options: UseAlertStreamOptions = {}): UseAlertStreamResult {
  const { isAuthenticated } = useAuth()
  const enabled = (options.enabled ?? true) && isAuthenticated
  const client = useQueryClient()

  const [status, setStatus] = useState<AlertStreamStatus>('connecting')
  const [recent, setRecent] = useState<readonly AlertEnvelope[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  // Held in a ref so the socket's own listener always sees the current callback
  // without the socket being rebuilt whenever the caller re-renders.
  const onAlertRef = useRef(options.onAlert)
  onAlertRef.current = options.onAlert

  const markRead = useCallback(() => setUnreadCount(0), [])

  const dismiss = useCallback((alertId: string) => {
    setRecent((current) => current.filter((envelope) => envelope.alertId !== alertId))
  }, [])

  useEffect(() => {
    if (!enabled) return

    const socket = new LiveSocket({
      ...(options.socketFactory === undefined ? {} : { factory: options.socketFactory }),
      handlers: {
        onEvent: (payload) => {
          // A reader drops what it cannot read: `isAlertEnvelope` checks the version
          // as well as the vocabulary, so a server one version ahead degrades to
          // "nothing arrived" rather than to a toast rendering `undefined`.
          if (!isAlertEnvelope(payload)) return
          const envelope = payload

          setRecent((current) => {
            if (current.some((existing) => existing.alertId === envelope.alertId)) return current
            const next = [envelope, ...current]
            return next.length > ALERT_STREAM_BUFFER ? next.slice(0, ALERT_STREAM_BUFFER) : next
          })
          setUnreadCount((count) => count + 1)
          onAlertRef.current?.(envelope)

          // The counts header is five integers and is cheap; the FEED is not, and is
          // deliberately left alone — see the header comment.
          void client.invalidateQueries({ queryKey: alertQueryKeys.counts })
        },
        onStatus: (socketStatus) => {
          setStatus(
            socketStatus === 'live'
              ? 'live'
              : socketStatus === 'connecting' || socketStatus === 'idle'
                ? 'connecting'
                : 'degraded',
          )
        },
      },
    })

    socket.start()
    return () => socket.stop()
    // Keyed on `enabled` alone. `socketFactory` is a test seam that never changes
    // within a mount, and including it would tear the socket down on every render of
    // a caller that builds the object inline.
  }, [enabled, client])

  return {
    status,
    degraded: status !== 'live',
    unreadCount,
    recent,
    markRead,
    dismiss,
  }
}
